import React from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { CreditSubmission, FinanceProgram } from '@trnsact/trnsact-shared-types';
import { Nullable } from 'global';
import { ReadOnlyQuill } from 'modules/desking/ui/components/catalogDetails/ReadOnlyQuill';
import { convertHtmlToDelta } from 'utils/quillUtils';

interface Props {
  selectedSubmission: Nullable<CreditSubmission>;
  selectedFinanceProgram: Nullable<FinanceProgram>;
}

export const LenderRequirementsDisplayWithFinanceProgramAndSubmission = ({
  selectedSubmission,
  selectedFinanceProgram,
}: Props) => {
  const classes = useStyles();

  const lenderName = selectedSubmission?.lenderName ?? '—';
  const programName = selectedFinanceProgram?.nameInternal ?? '—';

  const submissionStatus = selectedSubmission?.decision ?? '—';
  const submissionDate = selectedSubmission?.submissionDate ?? '—';

  const contact = selectedSubmission?.lenderContactInfo?.[0];
  const contactName = contact?.firstName ? `${contact?.firstName} ${contact?.lastName}` : '—';
  const contactEmail = contact?.email ?? '—';
  const contactPhone = contact?.phoneNumber ?? '—';

  const coverageHtml = selectedFinanceProgram?.insuranceCoverage?.coverageRequirements ?? '<p>—</p>';
  const certificateHtml = selectedFinanceProgram?.insuranceCoverage?.certificateRequirements ?? '<p>—</p>';
  const otherHtml = selectedFinanceProgram?.insuranceCoverage?.otherInstructions ?? '<p>—</p>';

  return (
    <Box className={classes.shell}>
      <Box className={classes.topBar}>
        <Box className={classes.topBarLeft}>
          <Box className={classes.topBarIcon}>
            <DescriptionOutlinedIcon className={classes.topBarIconSvg} />
          </Box>
          <Typography className={classes.topBarTitle}>Submission &amp; Finance Program</Typography>
        </Box>

        <Box className={classes.topBarRight}>
          <Button className={classes.topBarBtn} variant="outlined" disabled>
            CHANGE SUBMISSION
          </Button>
        </Box>
      </Box>

      {/* White content area */}
      <Box className={classes.body}>
        <Typography className={classes.pageTitle}>
          {lenderName} — {programName}
        </Typography>

        <Box className={classes.twoCol}>
          <Box className={classes.col}>
            <Typography className={classes.colTitle}>Submission</Typography>

            <Box className={classes.kvRow}>
              <Typography className={classes.kLabel}>Status:</Typography>
              <Typography className={classes.kValue}>{submissionStatus}</Typography>
            </Box>

            <Box className={classes.kvRow}>
              <Typography className={classes.kLabel}>Date of Submission:</Typography>
              <Typography className={classes.kValue}>{submissionDate}</Typography>
            </Box>
          </Box>

          <Box className={classes.col}>
            <Typography className={classes.colTitle}>Lender Contacts</Typography>

            <Typography className={classes.contactName}>{contactName}</Typography>

            <Box className={classes.contactLine}>
              <EmailOutlinedIcon className={classes.contactIcon} />
              <Typography className={classes.contactText}>{contactEmail}</Typography>
            </Box>

            <Box className={classes.contactLine}>
              <PhoneOutlinedIcon className={classes.contactIcon} />
              <Typography className={classes.contactText}>{contactPhone}</Typography>
            </Box>
          </Box>
        </Box>

        <Divider className={classes.divider} />

        <Typography className={classes.sectionTitle}>Insurance Requirements</Typography>

        <Box className={classes.requirementBlock}>
          <Typography className={classes.blockTitle}>Coverage Requirements</Typography>
          <Box className={classes.richText}>
            <ReadOnlyQuill value={convertHtmlToDelta(coverageHtml)} />
          </Box>
        </Box>

        <Divider className={classes.thinDivider} />

        <Box className={classes.requirementBlock}>
          <Typography className={classes.blockTitle}>Certificate Requirements</Typography>
          <Box className={classes.richText}>
            <ReadOnlyQuill value={convertHtmlToDelta(certificateHtml)} />
          </Box>
        </Box>

        <Divider className={classes.thinDivider} />

        <Box className={classes.requirementBlock}>
          <Typography className={classes.blockTitle}>Other Instructions</Typography>
          <Box className={classes.richText}>
            <ReadOnlyQuill value={convertHtmlToDelta(otherHtml)} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const useStyles = makeStyles({
  shell: {
    border: '1px solid #e5e7eb',
    borderRadius: 4,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },

  // gray header strip
  topBar: {
    backgroundColor: '#f3f4f6',
    borderBottom: '1px solid #e5e7eb',
    padding: '10px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  topBarLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0,
  },
  topBarIcon: {
    width: 26,
    height: 26,
    borderRadius: 4,
    backgroundColor: '#e5e7eb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  topBarIconSvg: {
    fontSize: 18,
    color: '#6b7280',
  },
  topBarTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#111827',
    whiteSpace: 'nowrap',
  },
  topBarRight: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
  },
  topBarBtn: {
    borderColor: '#93c5fd',
    color: '#2563eb',
    backgroundColor: '#eff6ff',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
    minHeight: 28,
    textTransform: 'none',
    borderRadius: 4,
  },

  body: {
    padding: 14,
    backgroundColor: '#ffffff',
  },
  pageTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#111827',
    marginBottom: 10,
  },

  twoCol: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 24,
    alignItems: 'start',
  },
  col: {
    minWidth: 0,
  },
  colTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 6,
  },

  kvRow: {
    display: 'flex',
    gap: 6,
    alignItems: 'baseline',
    marginBottom: 4,
  },
  kLabel: {
    fontSize: 11,
    color: '#6b7280',
  },
  kValue: {
    fontSize: 11,
    color: '#111827',
  },

  contactName: {
    fontSize: 11,
    color: '#111827',
    marginBottom: 4,
  },
  contactLine: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  contactIcon: {
    fontSize: 14,
    color: '#111827',
  },
  contactText: {
    fontSize: 11,
    color: '#111827',
  },

  divider: {
    margin: '12px 0',
    borderColor: '#e5e7eb',
  },

  sectionTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 8,
  },

  requirementBlock: {
    padding: '6px 0',
  },
  blockTitle: {
    fontSize: 11,
    fontWeight: 700,
    color: '#111827',
    marginBottom: 6,
  },
  thinDivider: {
    margin: '8px 0',
    borderColor: '#e5e7eb',
  },

  richText: {
    fontSize: 11,
    color: '#111827',
    '& .ql-container': {
      border: 'none',
    },
    '& .ql-editor': {
      padding: 0,
      fontSize: 11,
      lineHeight: 1.4,
      color: '#111827',
    },
    '& .ql-editor p': {
      margin: '0 0 6px 0',
    },
    '& .ql-editor ul': {
      margin: '0 0 6px 18px',
      padding: 0,
    },
    '& .ql-editor li': {
      marginBottom: 4,
    },
  },

  '@media (max-width: 900px)': {
    twoCol: {
      gridTemplateColumns: '1fr',
      gap: 12,
    },
  },
});
